import { useState, useMemo, useRef, useLayoutEffect } from "react";

//components
import Skeleton from "../../global/Skeleton";

//tools
import cc from "classcat";
import { $translation } from "../../../tools/helpers";
import { $price } from "../../../tools/helpers";
import { useTranslation } from "react-i18next";

//styles
import styles from "./ProductModalContent.module.css";

const getListMode = (selectLogic) => {
	if (selectLogic === "single") return "single";
	else if (selectLogic === "min_1") return "min_1";
	else return "multiple";
};

const getOptionsLogicText = (selectLogic, t) => {
	if (selectLogic === "single") return t("Choose one option");
	else if (selectLogic === "min_1") return t("Choose at least one option");
	else if (selectLogic === "multiple") return t("Choose any number of options");
	else return "";
};

const getOptionClass = (option, isSelected) => {
	return cc([
		"cng-menu-product-option",
		"cng-smart-list-item",
		option.Available === "false" && "cng-menu-product-option-not-available",
		isSelected && "selected",
	]);
};

const getOptionBullet = (isSelected) => {
	return (
		<i
			className={cc([
				"mdc-button__icon",
				isSelected
					? [
						"cng-smart-list-item-control-checked",
						"cng-vector-icon",
						"cng-vector-icon-16",
						"cng-vector-icon-green",
						"cng-vector-icon-check_circle_fill",
						styles["icon-visible"],
					]
					: [
						"cng-smart-list-item-control-unchecked",
						"cng-vector-icon",
						"cng-vector-icon-16",
						"cng-vector-icon-circle",
						styles["icon-visible"],
					],
			])}
		/>
	);
};

const ProductModalContent = ({
	product = {},
	isFixedPriceMenu = false,
	isMenuAvailable = true,
	isLoading = false,
}) => {
	const { t } = useTranslation();
	const optionsSections = product?.OptionsSections || [];
	

	const containerRef = useRef(null);
	const skeletonRef = useRef(null);
	const contentRef = useRef(null);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		let targetHeight;
		if (isLoading) {
			if (skeletonRef.current) {
				targetHeight = skeletonRef.current.offsetHeight;
				container.style.height = targetHeight + "px";
			}
		} else {
			if (contentRef.current) {
				targetHeight = contentRef.current.offsetHeight;
				container.style.height = targetHeight + "px";
				const timeout = setTimeout(() => {
					if (container) container.style.height = "auto";
				}, 400);
				return () => clearTimeout(timeout);
			}
		}
	}, [isLoading, product]);

	const [selected, setSelected] = useState(() => {
		const initial = {};
		optionsSections.forEach((section, idx) => {
			initial[idx] = [];
		});
		return initial;
	});

	const isOptionsValid = useMemo(() => {
		return optionsSections.every((section, idx) => {
			const mode =
				section.SelectLogic === "single"
					? "single"
					: section.SelectLogic === "min_1"
						? "min_1"
						: "multiple";
			if (mode === "single" || mode === "min_1") {
				return (selected[idx] || []).length > 0;
			}
			return true;
		});
	}, [optionsSections, selected]);

	const selectedOptionsTotal = useMemo(() => {
		let total = 0;
		optionsSections.forEach((section, sidx) => {
			const sel = selected[sidx] || [];
			sel.forEach((oidx) => {
				const option = section.Options?.[oidx];
				if (option && !isNaN(parseFloat(option.Price))) {
					total += parseFloat(option.Price);
				}
			});
		});
		return total;
	}, [optionsSections, selected]);

	const handleOptionClick = (sectionIdx, optionIdx, listMode) => {
		setSelected((prev) => {
			const sectionSelected = prev[sectionIdx] || [];
			let newSelected;
			if (listMode === "single") {
				newSelected = [optionIdx];
			} else if (listMode === "multiple") {
				newSelected = sectionSelected.includes(optionIdx)
					? sectionSelected.filter((i) => i !== optionIdx)
					: [...sectionSelected, optionIdx];
			} else if (listMode === "min_1") {
				if (sectionSelected.includes(optionIdx)) {
					if (sectionSelected.length > 1) {
						newSelected = sectionSelected.filter((i) => i !== optionIdx);
					} else {
						newSelected = sectionSelected;
					}
				} else {
					newSelected = [...sectionSelected, optionIdx];
				}
			} else {
				newSelected = sectionSelected;
			}
			return { ...prev, [sectionIdx]: newSelected };
		});
	};

	const handleAddToBasket = async () => {
		const tguid = window?.__cng_common?.table_guid;
		const pid = product?.ID;
		let options = [];
		optionsSections.forEach((section, sidx) => {
			const sel = selected[sidx] || [];
			sel.forEach((oidx) => {
				const option = section.Options?.[oidx];
				if (option?.ID) options.push(option.ID);
			});
		});
		const params = new URLSearchParams();
		if (tguid) params.append("tguid", tguid);
		if (pid) params.append("pid", pid);
		options.forEach((opt) => params.append("options[]", opt));
		try {
			const res = await __cng_common.api_service.get(
				`/add_product_to_basket/?${params.toString()}`
			);
			if (res?.success) {
				window.store.emit('hide_modal', 'product');
				window.store.emit('set_products_count', res.active_basket_items_count);
				__cng_common.websocket_service.addingProduct({ pid });
			}
		} catch (e) { }
	};

	const renderOptions = () => {
		if (!product?.OptionsSections || !product?.OptionsSections.length) {
			return null;
		}
		return (
			<div className='cng-menu-product-options-sections cng-smart-lists-group cng-modal-options' style={{ opacity: 1, maxHeight: 'none', overflow: 'visible', padding: '10px 0' }}>
				{product?.OptionsSections.map((section, sidx) => {
					const listMode = getListMode(section.SelectLogic);
					return (
						<div
							className='cng-menu-product-options-section'
							key={sidx}>
							{section.Name && (
								<div className='cng-menu-product-options-section-name'>
									{$translation(section.Name)}
								</div>
							)}
							{section.Description && (
								<div className='cng-menu-product-options-section-description'>
									{$translation(section.Description)}
								</div>
							)}
							<div className='cng-menu-product-options-logic'>
								<div>{getOptionsLogicText(section.SelectLogic, t)}</div>
							</div>
							<div className='cng-menu-product-options cng-smart-list'>
								{section.Options.map((option, oidx) => {
									const isSelected = (selected[sidx] || []).includes(oidx);
									return (
										<div
											className={getOptionClass(option, isSelected)}
											key={oidx}
											onClick={() =>
												option.Available !== "false" &&
												handleOptionClick(sidx, oidx, listMode)
											}>
											<div className='cng-menu-product-option-bullet cng-smart-list-item-control'>
												{getOptionBullet(isSelected)}
											</div>
											<div className='cng-menu-product-option-name'>
												{$translation(option.Name)}
											</div>
											{parseInt(option.Price) > 0 && (
												<>
													<div className='cng-menu-product-option-spacer'>
														&nbsp;
													</div>
													<div className='cng-menu-product-option-price'>
														{(parseInt(product?.Price) > 0 ? "+" : "") +
															$price(option.Price, true)} €
													</div>
												</>
											)}
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	const renderImage = () =>
		product?.Image_ID && Number(product.Image_ID) !== 0 ? (
			<div
				className='cng-menu-product-img shimmerBG'
				id={`${product?.Image_ID}_${product?.CroppedURL}`}>
				<img src={product?.CroppedURL} />
			</div>
		) : null;

	const renderFooter = () => {
		const basePrice = parseFloat(product?.Price) || 0;
		const discountedPrice = parseFloat(product?.DiscountedPrice) || 0;
		const isDiscountActive = product?.IsDiscountActive === true;
		const totalPrice = basePrice + selectedOptionsTotal;
		const totalDiscountedPrice = discountedPrice + selectedOptionsTotal;
		
		return (
			<div className='cng-menu-product-footer'>
				<div className='cng-menu-product-price'>
					{(basePrice > 0 || selectedOptionsTotal > 0) && (
						<>
							{isDiscountActive && discountedPrice > 0 ? (
								<div className='cng-menu-product-price-discounted'>
									<div className='cng-menu-product-price-original'>
										{$price(totalPrice, true)}{" "}
										{product?.Units !== "pc" && ` / ${product?.Units}`}
									</div>
									<div className='cng-menu-product-price-discount'>
										{$price(totalDiscountedPrice, true)}{" "}
										{product?.Units !== "pc" && ` / ${product?.Units}`}
									</div>
								</div>
							) : (
								<>
									{$price(totalPrice, true)}{" "}
									{product?.Units !== "pc" && ` / ${product?.Units}`}
								</>
							)}
						</>
					)}
				</div>
				{product?.Type !== "text" && (
					<div className='cng-menu-product-action'>
						<i
							className={cc([
								"mdc-button__icon",
								"cng-menu-product-in-basket-mark",
								"cng-in-menu-product-control",
								"cng-vector-icon",
								"cng-vector-icon-green",
								"cng-vector-icon-24",
								"cng-vector-icon-done_all",
							])}
							title={t("In basket")}
						/>
						<i
							className={cc([
								"mdc-button__icon",
								"cng-menu-product-add-to-basket",
								"cng-menu-product-action-control",
								"cng-vector-icon",
								"cng-vector-icon-20",
								"cng-vector-icon-add_circle",
								!isOptionsValid && "disabled",
							])}
							title={t("Add to basket")}
							onClick={handleAddToBasket}
						/>
					</div>
				)}
				<div className='cng-menu-product-not-available-sign'>
					<div className='cng-text'>{t("Not available")}</div>
					<i
						className={cc([
							"mdc-button__icon",
							"cng-menu-product-remove-from-basket",
							"cng-menu-product-action-control",
							"cng-basket-control",
							"cng-vector-icon",
							"cng-vector-icon-20",
							"cng-vector-icon-delete",
						])}
						title={t("Remove from basket")}
					/>
				</div>
			</div>
		);
	};

	const renderWarning = () => (
		<div className='cng-menu-product-warning cng-hidden'>
			<i
				className={cc([
					"cng-icon-exclamations",
					"cng-vector-icon",
					"cng-vector-icon-24",
					"cng-vector-icon-error",
				])}
			/>
			<div className='cng-menu-product-warning-text'>
				{t("Incorrect options.")}
			</div>
		</div>
	);

	if (isLoading) {
		return (
			<div
				ref={containerRef}
				className={cc([
					"cng-menu-product",
					"cng-smart-list-item",
					"cng-test",
					product?.HasOptions && "cng-menu-product-has-options",
					product?.Available === "false" && "cng-menu-product-not-available",
					!isMenuAvailable && "cng-menu-product-menu-not-available",
					isFixedPriceMenu && "cng-fixed-price-menu-product",
					styles.product,
				])}
			>
				<div ref={skeletonRef}>
					<Skeleton
						width='100%'
						height='180px'
						borderRadius='4px'
						margin='0 0 16px 0'
					/>
					<div className={styles["skeleton-content"]}>
						<Skeleton
							width='60%'
							height='16px'
							borderRadius='4px'
							margin='0 0 12px 0'
						/>
						<Skeleton
							width='90%'
							height='14px'
							borderRadius='4px'
							margin='0 0 8px 0'
						/>
						<Skeleton
							width='40%'
							height='14px'
							borderRadius='4px'
							margin='0 0 24px 0'
						/>
						<Skeleton
							width='100%'
							height='8px'
							borderRadius='4px'
							margin='0 0 8px 0'
						/>
						<Skeleton
							width='100%'
							height='8px'
							borderRadius='4px'
							margin='0 0 8px 0'
						/>
						<Skeleton
							width='100%'
							height='8px'
							borderRadius='4px'
							margin='0 0 8px 0'
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			ref={containerRef}
			className={cc([
				"cng-menu-product",
				"cng-smart-list-item",
				"cng-test",
				product?.HasOptions && "cng-menu-product-has-options",
				product?.Available === "false" && "cng-menu-product-not-available",
				!isMenuAvailable && "cng-menu-product-menu-not-available",
				isFixedPriceMenu && "cng-fixed-price-menu-product",
				styles.product,
			])}
		>
			<div ref={contentRef}>
				{renderImage()}
				<div className={cc(["cng-menu-product-info"])}>
					<div className='cng-menu-product-title'>
						{$translation(product?.Name)}
					</div>
					{product?.Description && (
						<div className='cng-menu-product-description'>
							{$translation(product?.Description)}
						</div>
					)}
					{renderWarning()}
					{product?.HasOptions && renderOptions()}
					{renderFooter()}
				</div>
			</div>
		</div>
	);
};

export default ProductModalContent;

